/**
 * ScrollFade — fades the edge(s) of a scroll container to alpha 0, but ONLY on
 * the side that has hidden content. At the top of the scroll the top fade is
 * removed (nothing is cut off there); at the bottom the bottom fade is removed;
 * in the middle both fade; with no overflow there's no fade at all.
 *
 * Imperative (like ScrollArrows / OverlayScrollbar): it mutates the target's
 * mask-image directly on scroll/resize/mutation rather than re-rendering, so it
 * never costs a React render. Renders nothing.
 *
 *   <div ref={ref} style={{ overflowY: 'auto' }}>…</div>
 *   <ScrollFade targetRef={ref} />
 */

import { useEffect, useRef } from 'react';

interface Props {
  targetRef: React.RefObject<HTMLElement | null>;
  /** Fade length in px at each faded edge. */
  size?: number;
  /** Minimum overflow (px) before any fade shows. */
  threshold?: number;
}

const grad = (top: boolean, bottom: boolean, f: number): string => {
  const start = top ? `transparent 0, #000 ${f}px` : '#000 0';
  const end = bottom ? `#000 calc(100% - ${f}px), transparent 100%` : '#000 100%';
  return `linear-gradient(to bottom, ${start}, ${end})`;
};

export default function ScrollFade({ targetRef, size = 36, threshold = 8 }: Props) {
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;

    const apply = (mask: string) => {
      el.style.setProperty('-webkit-mask-image', mask || 'none');
      el.style.setProperty('mask-image', mask || 'none');
    };

    const compute = () => {
      rafRef.current = undefined;
      const overflow = el.scrollHeight - el.clientHeight;
      if (overflow <= threshold) { apply(''); return; }
      const atTop = el.scrollTop <= 4;
      const atBottom = el.scrollTop >= overflow - 4;
      // Fade a side only when content is hidden past it.
      apply(grad(!atTop, !atBottom, size));
    };
    const update = () => {
      if (rafRef.current == null) rafRef.current = requestAnimationFrame(compute);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
    window.addEventListener('resize', update);

    return () => {
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = undefined; }
      el.removeEventListener('scroll', update);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', update);
      apply('');
    };
  }, [targetRef, size, threshold]);

  return null;
}
